import React, { useEffect, useState } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import classnames from "classnames";

import { Loader, SelectInput, ZeroToInfiniteGrid } from "~/core";
import { IGenericOption } from "~/core/components/select-input/model";
import { messages } from "../../../../i18n-messages";

import "./event-surface-stats-table.css";

interface IEventSurfaceStatsTableProps {
    tableRecords: ITableRecord[];
    hidden: boolean;
    id: string;
    intl: intlShape;
    isLoading: boolean;
}

interface ITableRecord {
    avg: string;
    layer: string;
    max: string;
    min: string;
    season: string;
    surface: string;
}

const EventSurfaceStatsTable_ = (props: IEventSurfaceStatsTableProps): JSX.Element => {
    const { hidden, isLoading, tableRecords, intl } = props;

    const [filteredRecords, setFilteredRecords] = useState<ITableRecord[]>(null);
    const [layer, setLayer] = useState<string>(null);
    const [season, setSeason] = useState<string>(null);
    const [surface, setSurface] = useState<string>(null);

    useEffect(() => {
        _filterRecords();
    }, [layer, season, surface]);

    useEffect(() => {
        if (tableRecords?.length) {
            _filterRecords();
        }
    }, [tableRecords]);

    const { formatMessage } = intl;

    const selectRow = (
        <div
            key="select-row"
            className={classnames("surface-display-select-row", {
                "right-aligned-row": true,
            })}
        >
            <SelectInput
                containerClassNames={["surface-display-layer-list"]}
                placeholderText={formatMessage(messages.splitScreenSeasonLbl)}
                value={season}
                options={_seasonOptions()}
                onChange={(s) => _selectSeasonOption(s)}
                testId="event-surface-stats-season-select"
            />
            <SelectInput
                containerClassNames={["surface-display-layer-list"]}
                placeholderText={formatMessage(messages.splitScreenLayerLbl)}
                value={layer}
                options={_layerOptions()}
                onChange={(l) => _selectLayerOption(l)}
                testId="event-surface-stats-layer-select"
            />
            <SelectInput
                containerClassNames={["surface-display-layer-list"]}
                placeholderText={formatMessage(messages.splitScreenSurfaceLbl)}
                value={surface}
                options={_surfaceOptions()}
                onChange={(s) => _selectSurfaceOption(s)}
                testId="event-surface-stats-surface-select"
            />
        </div>
    );
    const table = tableRecords.length ? (
        <ZeroToInfiniteGrid
            columns={_getColumns()}
            records={filteredRecords}
            className={classnames("event-surface-stats-table", isLoading ? "hide" : "")}
        />
    ) : null;

    return (
        <div className={classnames("surface-display-container", hidden ? "hide" : "")}>
            {selectRow}
            {table}
            {isLoading ? <Loader className="loader" /> : null}
        </div>
    );

    function _layerOptions() {
        if (filteredRecords == null) {
            return [];
        }
        const records = season || surface ? filteredRecords : tableRecords;
        return records
            .reduce((layers: IGenericOption<string>[], rec: ITableRecord) => {
                const existingItem = layers.find((s) => s.value === rec.layer);
                if (!existingItem) {
                    layers.push({
                        label: rec.layer,
                        value: rec.layer,
                    });
                }
                return layers;
            }, [])
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    function _seasonOptions() {
        if (filteredRecords == null) {
            return [];
        }
        const records = layer || surface ? filteredRecords : tableRecords;
        return records
            .reduce((seasons: IGenericOption<string>[], rec: ITableRecord) => {
                const existingItem = seasons.find((s) => s.value === rec.season);
                if (!existingItem) {
                    seasons.push({
                        label: rec.season,
                        value: rec.season,
                    });
                }
                return seasons;
            }, [])
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    function _selectLayerOption(layer) {
        setLayer(layer);
    }

    function _selectSeasonOption(season) {
        setSeason(season);
    }

    function _selectSurfaceOption(surface) {
        setSurface(surface);
    }

    function _surfaceOptions() {
        if (filteredRecords == null) {
            return [];
        }
        const records = layer || season ? filteredRecords : tableRecords;
        return records
            .reduce((surfaces: IGenericOption<string>[], rec: ITableRecord) => {
                const existingItem = surfaces.find((s) => s.value === rec.surface);
                if (!existingItem) {
                    surfaces.push({
                        label: rec.surface,
                        value: rec.surface,
                    });
                }
                return surfaces;
            }, [])
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    function _filterRecords() {
        if (tableRecords.length) {
            const filteredRecords = tableRecords.filter((r) => {
                return (
                    (!season || r.season === season) &&
                    (!layer || r.layer === layer) &&
                    (!surface || r.surface === surface)
                );
            });
            setFilteredRecords(filteredRecords);
        } else {
            setSeason(null);
            setLayer(null);
            setSurface(null);
            setFilteredRecords(null);
        }
    }

    function _getColumns() {
        if (!tableRecords.length) {
            return {};
        }
        const columnNames = Object.keys(tableRecords[0]);
        return columnNames.reduce((columns, col) => {
            columns[col] = {
                title: col,
                label: col,
                className: col,
            };
            return columns;
        }, {});
    }
};

export const EventSurfaceStatsTable = injectIntl(EventSurfaceStatsTable_);
